import React, {
  ReactNode,
  HTMLAttributes,
  UIEventHandler,
  CSSProperties,
  ImgHTMLAttributes,
  MouseEventHandler,
  useState,
  createContext,
  useContext,
} from "react";
import clsx from "clsx";

// Pressable
type PressableProps = HTMLAttributes<HTMLDivElement> & {
  onPress?: MouseEventHandler<HTMLDivElement>;
  children?: ReactNode;
  style?: CSSProperties;
};

const Pressable = ({ children, onPress, style, className, ...props }: PressableProps) => (
  <div
    role="button"
    tabIndex={0}
    className={clsx("default_pressable", className)}
    style={style}
    onClick={onPress}
    {...props}
  >
    {children}
  </div>
);

// ScrollView
type ScrollViewProps = HTMLAttributes<HTMLDivElement> & {
  children?: ReactNode;
  horizontal?: boolean;
  onScroll?: UIEventHandler<HTMLDivElement>;
  style?: CSSProperties;
};

const ScrollView = ({
  children,
  horizontal,
  style,
  onScroll,
  className,
  ...props
}: ScrollViewProps) => (
  <div
    className={clsx("default_scrollview", className)}
    style={{
      overflowX: horizontal ? "auto" : "hidden",
      overflowY: horizontal ? "hidden" : "auto",
      ...style,
    }}
    onScroll={onScroll}
    {...props}
  >
    {children}
  </div>
);

// Image
type ImageProps = ImgHTMLAttributes<HTMLImageElement>;
const Image = (props: ImageProps) => <img className="default_image" {...props} />;

// ActivityIndicator
const ActivityIndicator = () => (
  <div className="default_activity_indicator" />
);

// TouchableOpacity
type TouchableOpacityProps = HTMLAttributes<HTMLDivElement> & {
  onPress?: MouseEventHandler<HTMLDivElement>;
  children?: ReactNode;
};

const TouchableOpacity = ({
  children,
  onPress,
  className,
  ...props
}: TouchableOpacityProps) => {
  const [active, setActive] = useState(false);

  return (
    <div
      role="button"
      tabIndex={0}
      className={clsx(
        "default_touchable_opacity",
        active && "default_touchable_opacity_active ",
        className
      )}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
      onMouseLeave={() => setActive(false)}
      onClick={onPress}
      {...props}
    >
      {children}
    </div>
  );
};

// FlatList
type FlatListProps<T> = {
  data: T[];
  renderItem: (item: T, index: number) => React.ReactNode;
  keyExtractor?: (item: T, index: number) => string;
};

function FlatList<T>({ data, renderItem, keyExtractor }: FlatListProps<T>) {
  return (
    <div className="default_flatlist">
      {data.map((item, index) => (
        <div key={keyExtractor ? keyExtractor(item, index) : index}>
          {renderItem(item, index)}
        </div>
      ))}
    </div>
  );
}

// ImageBackground
type ImageBackgroundProps = HTMLAttributes<HTMLDivElement> & {
  source: string;
  resizeMode?: "cover" | "contain" | "stretch" | "center";
  children?: ReactNode;
  style?: CSSProperties;
};

const ImageBackground = ({
  source,
  resizeMode = "cover",
  children,
  style,
  className,
  ...props
}: ImageBackgroundProps) => {
  const backgroundSizeMap: Record<string, string> = {
    cover: "cover",
    contain: "contain",
    stretch: "100% 100%",
    center: "auto",
  };

  return (
    <div
      className={clsx("w-full h-full flex flex-col", className)}
      style={{
        backgroundImage: `url(${source})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: backgroundSizeMap[resizeMode],
        ...style,
      }}
      {...props}
    >
      {children}
    </div>
  );
};

// Modal
type ModalProps = {
  children: ReactNode;
  open: boolean;
  onClose?: () => void;
};

const Modal = ({ open, onClose, children }: ModalProps) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()}>{children}</div>
    </div>
  );
};

// Dialog
type DialogProps = {
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  open: boolean;
};

const Dialog = ({
  open,
  title,
  description,
  confirmText = "OK",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
}: DialogProps) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div
        className="bg-white p-6 rounded-md w-full max-w-sm shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-gray-600 mb-4">{description}</p>
        <div className="flex justify-end gap-3">
          <button
            className="px-4 py-2 rounded text-gray-600 hover:bg-gray-100"
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Toast
type ToastContextType = {
  showToast: (msg: string) => void;
};

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("Wrap your app in <ToastProvider>");
  return ctx;
};

const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [message, setMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(null), 3000);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {message && (
        <div className="fixed bottom-5 left-1/2 -translate-x-1/2 bg-gray-800 text-white px-4 py-2 rounded shadow-md z-[1000]">
          {message}
        </div>
      )}
    </ToastContext.Provider>
  );
};

// SectionList
type SectionListProps<T> = {
  sections: {
    title: string;
    data: T[];
  }[];
  renderItem: (item: T, index: number) => React.ReactNode;
  renderSectionHeader?: (title: string, index: number) => React.ReactNode;
};

function SectionList<T>({
  sections,
  renderItem,
  renderSectionHeader,
}: SectionListProps<T>) {
  return (
    <div className="space-y-4">
      {sections.map((section, i) => (
        <div key={i}>
          {renderSectionHeader ? (
            renderSectionHeader(section.title, i)
          ) : (
            <h4 className="text-lg font-semibold mb-2">{section.title}</h4>
          )}
          <div className="space-y-2">
            {section.data.map((item, j) => (
              <div key={j}>{renderItem(item, j)}</div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// Overlay
type OverlayProps = {
  visible: boolean;
  onClick?: () => void;
  children?: ReactNode;
};

const Overlay = ({ visible, onClick, children }: OverlayProps) => {
  if (!visible) return null;
  return (
    <div
      onClick={onClick}
      className="default_overlay"
    >
      {children}
    </div>
  );
};

// Export all components
export {
  FlatList,
  TouchableOpacity,
  Pressable,
  ScrollView,
  ActivityIndicator,
  Image,
  ImageBackground,
  Modal,
  Dialog,
  SectionList,
  Overlay,
  ToastProvider,
  useToast,
};
